import {noop} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {forwardSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import {
  RegistrationTableBaseProps,
  RegistrationTableConfig as RegistrationTableConfigData,
} from 'notebook/registration-tables/RegistrationTableConstants';
import RegistrationTableSettingsModal, {
  RegistrationTableSettingsModalValues,
} from 'notebook/registration-tables/RegistrationTableSettingsModal';
import {
  getColumnDefinitionsFromStructuredTableColumns,
  getInsertableColumnInfos,
  getRegistrationTableRows,
  performColumnOpAndSetLabelingStrategy,
} from 'notebook/registration-tables/RegistrationTableUtil';
import withRegistrationTableUtils, {
  RegistrationTableUtilitiesProps,
} from 'notebook/registration-tables/withRegistrationTableUtils';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import ManageSourcesModal from 'notebook/structured-tables/components/modals/ManageSourcesModal';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import {
  LegacyStructuredTableRow,
  SchemaFieldColumnUpdate,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import LookupField from 'notebook/tables/excess/functions/lookupField';
import {LookupType} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import {
  handleRefreshTableColumns,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {Cell, RowData, Table, TableData, TableHeaderControlsArgs} from 'notebook/tables/TableConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ModalAnchor from 'ui-library/ModalAnchor';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {INPUT_SAMPLES_SOURCE_UUID} from 'workflows/WorkflowConstants';

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class RegistrationTableConfigConnector extends DataConnector<RegistrationTableConfigProps, ConnectedProps> {
  stores = [SchemaFieldSelectorStore];

  enhanceProps(): ConnectedProps {
    const schemaFields = [
      ...this.props.tagSchema.fields,
      ...(this.props.batchSchema == null ? [] : this.props.batchSchema.fields),
    ];
    return {
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(schemaFields)
      ),
    };
  }
}

type RegistrationTableConfigProps = RegistrationTableBaseProps;

interface State {
  isManageSourcesModalOpen: boolean;
}

/**
 * This is a legacy registration table config, not a StructuredTable.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export class RegistrationTableConfig extends React.Component<
  RegistrationTableConfigProps & ConnectedProps & RegistrationTableUtilitiesProps,
  State
> {
  state: State = {
    isManageSourcesModalOpen: false,
  };

  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  _handleOpenManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: true});
  };

  _handleCloseManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: false});
  };

  _handleSubmitSettingsModal = (
    registrationTableSettingsModalValues: RegistrationTableSettingsModalValues
  ): void => {
    const table = this.getTable();
    const {labelingStrategy} = registrationTableSettingsModalValues;
    if (labelingStrategy != null) {
      performColumnOpAndSetLabelingStrategy({
        table,
        performTableOpOnTable: ({op, options}) => {
          this.props.editorProps.performTableOp({index: this.props.index, op, options});
        },
        labelingStrategy,
      });
    }
  };

  setIsWatchingInputTable(isWatchingInputTable: boolean): void {
    this.props.updateHandler(this.props.index, {isWatchingInputTable});
  }

  _handleChangeManageSources = (sourceTables: Array<string>, newSources: Array<string>): void => {
    this.setIsWatchingInputTable(sourceTables.concat(newSources).includes(INPUT_SAMPLES_SOURCE_UUID));
    this._handleCloseManageSourcesModal();
  };

  _handleOpenSettingsModal = (): void => {
    runModal(RegistrationTableSettingsModal, {
      labelingStrategy: this.props.labelingStrategy,
      locked: this.props.editorProps.locked,
      onRequestSubmit: this._handleSubmitSettingsModal,
      tagSchemaId: this.props.tagSchema.id,
    });
  };

  _renderSettingsModalButton(): JSX.Element | null {
    return (
      <Tooltip title="Settings">
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-left"
            icon="settings"
            onClick={this._handleOpenSettingsModal}
            size={ButtonSize.MINI}
          />
        </span>
      </Tooltip>
    );
  }

  _renderManageSourcesModalButton(): JSX.Element | null {
    if (APIUtil.apiIdToType(this.props.editorProps.fileId) !== ModelTypeConstants.STAGE_CONFIGURATION) {
      return null;
    }
    const sources = this.props.isWatchingInputTable ? [INPUT_SAMPLES_SOURCE_UUID] : [];
    const multipleRows = this.props.rows.length > 1;
    return (
      <>
        <ModalAnchor open={this.state.isManageSourcesModalOpen}>
          {this.state.isManageSourcesModalOpen && (
            <ManageSourcesModal
              initialSourceTableUuids={sources}
              onRequestClose={this._handleCloseManageSourcesModal}
              onRequestSubmit={this._handleChangeManageSources}
            />
          )}
        </ModalAnchor>
        <Tooltip
          title={`Manage sources${
            multipleRows ? '\nDisabled in templates for tables with more than one row' : ''
          }`}
        >
          <span>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className="u-mini-spacing-left"
              disabled={this.props.editorProps.locked || multipleRows}
              icon="sources"
              onClick={this._handleOpenManageSourcesModal}
              size={ButtonSize.MINI}
              text={sources.length.toString()}
            />
          </span>
        </Tooltip>
      </>
    );
  }

  _renderHeaderControls({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null {
    if (isCollapsed) {
      return null;
    }
    const doRefresh = async (): Promise<void> => {
      await handleRefreshTableColumns({
        entryOrTemplateId: this.props.editorProps.fileId,
        provenanceUuid: this.props.provenanceUuid,
        onUpdateTable: ({
          columnUpdates,
        }: {
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            this.props.table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...this.props.table, columnConstraints};
          this.props.updateHandler(this.props.index, {table: newTable});
        },
      });
    };

    return (
      <>
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Registration table config"
          disabled={this.props.editorProps.locked}
        />
        {this._renderSettingsModalButton()}
        {this._renderManageSourcesModalButton()}
      </>
    );
  }

  _getColumnDefinitions(): Array<ColumnDefinition> {
    return getColumnDefinitionsFromStructuredTableColumns(this.props.structuredTableColumns, {
      tagSchema: this.props.tagSchema,
      batchSchema: this.props.batchSchema,
      stageRuns: [],
    });
  }

  getTable = (): Table => ({
    ...this.props.table,
    metadata: {labelingStrategy: this.props.labelingStrategy},
  });

  _getRegistrationTableData({
    newColumns,
    newRows,
    labelingStrategy,
  }: {
    newColumns?: Array<StructuredTableColumn>;
    newRows?: Array<LegacyStructuredTableRow>;
    labelingStrategy?: RegistryLabelingStrategy;
  }): RegistrationTableConfigData {
    return {
      ...this.props.utils.getRegistrationTableData({newColumns, newRows, labelingStrategy}),
      type: NoteLinkedObjectType.REGISTRATION_TABLE_CONFIG,
    };
  }

  _handleUpdate = (index: number, {table}: {table: TableData}): void => {
    const newColumns: Array<StructuredTableColumn> = table.columnConstraints.map((constraint, i) => ({
      ...constraint!.structuredTableColumn!,
      width: table.widths[i],
    }));
    // handle new rows added via pull-down
    const newRowCells = table.table.slice();
    const newRowData = table.rowData!.slice();
    if (newRowCells.length > 1) {
      this.setIsWatchingInputTable(false);
    }
    for (let i = newRowData.length; i < newRowCells.length; i++) {
      newRowData.push({isFinalized: false});
    }

    this.props.utils.mergeRowsAndColumnsIntoTable(
      index,
      this._getRegistrationTableData({
        newColumns,
        newRows: getRegistrationTableRows(newRowCells, newRowData),
      }),
      []
    );
  };

  render(): JSX.Element {
    const entryType = APIUtil.apiIdToType(this.props.editorProps.fileId);
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );

    const table = this.getTable();
    return (
      <TableEditable
        {...allEditorProps}
        additionalFunctions={
          APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)
            ? []
            : [
                {
                  name: LookupField.name,
                  asyncOnClick: ({
                    rowData,
                    onUpdateSelectedCell,
                  }: {
                    rowData: RowData | null;
                    onUpdateSelectedCell: (cellData: Partial<Cell>) => void;
                  }) =>
                    LookupField.onCreateFormula({
                      entryType,
                      stageConfigurationId: (() => {
                        assert(
                          entryType === ModelTypeConstants.STAGE_CONFIGURATION,
                          'Expected entry to be a stage entry template'
                        );
                        return this.props.editorProps.fileId;
                      })(),
                      supportedLookupTypes: [LookupType.STAGE_RUN_INPUT, LookupType.EXPERIMENTAL_CONDITION],
                      rowData,
                      onUpdateSelectedCell,
                    }),
                },
              ]
        }
        cid={this.props.cid}
        computedTable={this.props.computedTable}
        disableColumnLocking={true}
        disableFormulas={false}
        disableLegacyHeader={true}
        headerControls={(args) => this._renderHeaderControls(args)}
        icon={this.props.icon}
        indentation={this.props.indentation}
        index={this.props.index}
        insertableColumnInfos={getInsertableColumnInfos(
          this.props.tagSchema,
          this.props.batchSchema,
          this.props.structuredTableColumns,
          this.props.selectorsById
        )}
        isCollapsible={true}
        isLeftAlignColumnHeader={true}
        isMaximized={this.props.isMaximized}
        disableMergingCells={true}
        metadataForRestore={this.props.metadataForRestore}
        mouseDownHandler={this.props.mouseDownHandler}
        mouseOverHandler={this.props.mouseOverHandler}
        multiEditing={this.props.multiEditing}
        openManageSelectorsModal={noop}
        selectorsEnabled={false}
        selectorsLink={RegistrationTableConfig.selectorsLink}
        showExcessLabels={this.props.showExcessLabels}
        showFormulaBox={true}
        tableLabel={this.props.tableTypeLabel}
        table={table}
        tableNamespace={this.props.tableNamespace}
        updateHandler={this._handleUpdate}
        {...forwardSearchResultProps(this.props)}
      />
    );
  }
}

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default RegistrationTableConfigConnector.connect(withRegistrationTableUtils(RegistrationTableConfig));
